import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SeverityBadge, DeficiencyStatusBadge } from "./ElectricalStatusBadge";
import { AlertTriangle, CheckCircle2, Calendar } from "lucide-react";

function ResolveModal({ deficiency, onSave, onClose }) {
  const [form, setForm] = useState({
    corrective_action: deficiency.corrective_action || "",
    resolution_date: deficiency.resolution_date || new Date().toISOString().split("T")[0],
    status: "resolved",
    notes: deficiency.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await hosys.entities.ElectricalDeficiency.update(deficiency.id, form);
    setSaving(false);
    onSave();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Resoldre deficiència</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
            <p className="font-medium">{deficiency.description}</p>
            <p className="text-xs text-gray-500 mt-1">{deficiency.installation_name}</p>
          </div>
          <div>
            <Label>Acció correctiva aplicada</Label>
            <Textarea value={form.corrective_action} onChange={e => setForm(f => ({ ...f, corrective_action: e.target.value }))} rows={2} required />
          </div>
          <div>
            <Label>Data de resolució</Label>
            <Input type="date" value={form.resolution_date} onChange={e => setForm(f => ({ ...f, resolution_date: e.target.value }))} />
          </div>
          <div>
            <Label>Estat</Label>
            <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="resolved">Resolta</SelectItem>
                <SelectItem value="accepted_risk">Risc acceptat</SelectItem>
                <SelectItem value="in_progress">En curs</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel·lar</Button>
            <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">
              {saving ? "Desant..." : "Marcar resolta"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function DeficiencyList({ deficiencies, onRefresh }) {
  const [resolving, setResolving] = useState(null);

  const open = deficiencies.filter(d => d.status === "open" || d.status === "in_progress");
  const closed = deficiencies.filter(d => d.status === "resolved" || d.status === "accepted_risk");

  const renderItem = (d) => (
    <div key={d.id} className="flex items-start justify-between gap-3 p-3 bg-white border border-gray-100 rounded-xl">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          {d.code && <span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded">{d.code}</span>}
          <SeverityBadge severity={d.severity} />
          <DeficiencyStatusBadge status={d.status} />
        </div>
        <p className="text-sm font-medium text-gray-900">{d.description}</p>
        <p className="text-xs text-gray-500 mt-0.5">{d.installation_name}</p>
        {d.deadline_date && (
          <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Límit: {new Date(d.deadline_date).toLocaleDateString("ca-ES")}
          </p>
        )}
        {d.corrective_action && (
          <p className="text-xs text-gray-500 mt-1 italic">{d.corrective_action}</p>
        )}
      </div>
      {(d.status === "open" || d.status === "in_progress") && (
        <Button size="sm" variant="outline" onClick={() => setResolving(d)} className="flex-shrink-0 gap-1 text-green-700 border-green-200 hover:bg-green-50">
          <CheckCircle2 className="w-3.5 h-3.5" /> Resoldre
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {open.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-semibold text-gray-700">Obertes ({open.length})</span>
          </div>
          <div className="space-y-2">{open.map(renderItem)}</div>
        </div>
      )}
      {closed.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span className="text-sm font-semibold text-gray-700">Resoltes ({closed.length})</span>
          </div>
          <div className="space-y-2">{closed.map(renderItem)}</div>
        </div>
      )}
      {deficiencies.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-6">No hi ha deficiències registrades</p>
      )}

      {resolving && (
        <ResolveModal
          deficiency={resolving}
          onSave={() => { setResolving(null); onRefresh(); }}
          onClose={() => setResolving(null)}
        />
      )}
    </div>
  );
}